'use client';

import { useLanguage } from '@bells/core/i18n';
import {
  attendantSedCardGos,
  attendantSedCardNoGos,
  attendantSedCarToys,
} from '@bells/i18n';
import { AttendantSearchSingleResponse } from '@bells/schema/models/attendant';
import { Badge } from '@bells/ui/components/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@bells/ui/components/card';

import {
  GosStatuses,
  NoCustomerStatuses,
  ThreesomeStatuses,
  Toys,
} from '@bells/types/attendant';
import { useMemo } from 'react';

export const GirlGos = ({ data }: { data?: AttendantSearchSingleResponse }) => {
  const { language } = useLanguage();

  const gos = useMemo(() => {
    return GosStatuses(language);
  }, [language]);

  const nogos = useMemo(() => {
    if (!data) {
      return [];
    }
    return Object.entries(gos)
      .filter((df) => !data.gos?.includes(df[0]))
      .map((df) => df[0]);
  }, [language, gos, data]);

  const threesomeStatuses = useMemo(() => {
    if (!data) {
      return [];
    }
    const statusses = ThreesomeStatuses(language);
    return statusses.filter((df) => data.threesome?.includes(df.value));
  }, [language, data]);

  const noThreesomeStatuses = useMemo(() => {
    if (!data) {
      return [];
    }
    const statusses = ThreesomeStatuses(language);
    return statusses.filter((df) => !data.threesome?.includes(df.value));
  }, [language, data]);

  const toys = useMemo(() => {
    if (!data) {
      return [];
    }
    return Toys(language);
  }, [language]);

  const noCustomersStatusses = useMemo(() => {
    if (!data) {
      return [];
    }
    const statusses = NoCustomerStatuses(language);
    return Object.entries(statusses)
      .filter((df) => !data.noCustomers?.includes(df[0]))
      .map((df) => df[1]);
  }, [language, data]);

  const noCustomersStatussesNo = useMemo(() => {
    if (!data) {
      return [];
    }
    const statusses = NoCustomerStatuses(language);
    return Object.entries(statusses)
      .filter((df) => data.noCustomers?.includes(df[0]))
      .map((df) => df[1]);
  }, [language, data]);

  return (
    <div className='grid grid-cols-1'>
      {data && data.gos != null && data.gos.length > 0 && (
        <Card className='m-0 w-full border-0 bg-transparent p-0 shadow-none'>
          <CardHeader className='px-0 pt-0'>
            <h3 className='font-semibold leading-none tracking-tight'>
              {attendantSedCardGos()}
            </h3>
          </CardHeader>
          <CardContent className='flex w-full flex-col gap-6 space-y-4 px-0'>
            <div className='inline-flex flex-wrap gap-2 font-medium'>
              {threesomeStatuses.map((df) => {
                return (
                  <Badge
                    className='break-all text-[14px] font-normal'
                    key={df.value}
                  >
                    {df.label}
                  </Badge>
                );
              })}
              {noCustomersStatusses.map((df) => {
                return (
                  <Badge
                    variant={'outline'}
                    className='break-all bg-green-200 text-[14px] font-normal text-green-700'
                    key={df}
                  >
                    {df}
                  </Badge>
                );
              })}
              {data.gos.map((df) => {
                return (
                  <Badge
                    variant={'outline'}
                    className='break-all bg-green-200 text-[14px] font-normal text-green-700'
                    key={df}
                  >
                    {gos[df]}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className='m-0 w-full border-0 bg-transparent p-0 shadow-none'>
        <CardHeader className='px-0'>
          <h3 className='font-semibold leading-none tracking-tight'>
            {attendantSedCardNoGos()}
          </h3>
        </CardHeader>
        <CardContent className='flex w-full flex-col gap-6 space-y-4 px-0'>
          <div className='inline-flex flex-wrap gap-2 font-medium'>
            {noThreesomeStatuses.map((df) => {
              return (
                <Badge
                  variant={'outline'}
                  className='break-all bg-red-200 text-[14px] font-normal text-red-700'
                  key={df.value}
                >
                  {df.label}
                </Badge>
              );
            })}
            {noCustomersStatussesNo.map((df) => {
              return (
                <Badge
                  variant={'outline'}
                  className='break-all bg-red-200 text-[14px] font-normal text-red-700'
                  key={df}
                >
                  {df}
                </Badge>
              );
            })}
            {(nogos ?? []).map((df) => {
              return (
                <Badge
                  variant={'outline'}
                  className='break-all bg-red-200 text-[14px] font-normal text-red-700'
                  key={df}
                >
                  {gos[df]}
                </Badge>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {data && data.toys != null && data.toys.length > 0 && (
        <Card className='m-0 w-full border-0 bg-transparent p-0 shadow-none'>
          <CardHeader className='px-0'>
            <CardTitle>{attendantSedCarToys()}</CardTitle>
          </CardHeader>

          <CardContent className='flex w-full flex-col gap-6 space-y-4 px-0'>
            <div className='inline-flex flex-wrap gap-2 font-medium'>
              {data.toys.map((df) => {
                return (
                  <Badge
                    className='break-all text-[14px] font-normal'
                    variant={'outline'}
                    key={df}
                  >
                    {toys[df]}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
